/**
 * The UIContainerPeer manages the client-side rendering of an MCOS AppContainer.  It constructs a UIContainer
 * corresponding to the AppContainer and synchronizes it using a peer model.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/Deferred",
    "dojo/dom-construct",
    "dojo/on",
    'dijit/layout/ContentPane',
    "mw-messageservice/MessageService",
    "mw-peermodel/PeerModelManager",
    "MW/uiframework/constants/TypeConstants",
    "MW/uiframework/CssLoader",
    "MW/uiframework/UIBuilderFactory",
    "MW/uiframework/UIContainer",
    "MW/uiframework/uicontainer/ChildWindowManager",
    "MW/uiframework/uicontainer/peer/PeerNodeStatefulBridge",
    "MW/uiframework/uicontainer/peer/PropertyTranslator",
    "mw-actiondataservice/ActionDataService",
    "mw-log/Log",
    "exports"
], function (declare, lang, Deferred, domConstruct, on, ContentPane, MessageService, PeerModelManager, TypeConstants,
             CssLoader, UIBuilderFactory, UIContainer, ChildWindowManager, PeerNodeStatefulBridge, PropertyTranslator,
             ActionDataService, Log, exports) {

    /**
     * @class UIContainerPeer
     */
    var UIContainerPeer = declare(null, {

        constructor: function (args) {
            var that = this;
            this.id = args.uiContainer ? args.uiContainer.id : args.id;
            this.serverChannel = args.serverChannel;
            this.clientChannel = args.clientChannel;
            this.toolstripChannel = args.toolstripChannel;
            this._uiContainer = args.uiContainer;
            this._deferredEvents = [];

            if (!MessageService.isRunning()) {
                MessageService.start();
            }
            this._serverModelManager = PeerModelManager.getInstance(this.serverChannel);
            this._serverModelManager.setSyncEnabled(true);
            this._serverModelManager.addEventListener("rootSet", this._handleServerRootSet, this);
            if (this.clientChannel) {
                this._clientModelManager = PeerModelManager.getServerInstance(this.clientChannel);
                this._clientModelManager.setSyncEnabled(true);
            }

            if (!this._uiContainer) {
                // Construct the UIBuilder to be used by the UIContainer.
                // This needs to be done before setting up the toolstrip peer model so that the builder gets notified
                // when the toolstrip peer model root is set.
                this._uiBuilder = UIBuilderFactory.getInstance({
                    isMATLABDriven: !!this.toolstripChannel,
                    channel : this.toolstripChannel,
                    manageTabGroupsOutsidePeerModel: true
                });
            }

            if (this.toolstripChannel) {
                var toolstripPeerModelManager = PeerModelManager.getInstance(this.toolstripChannel);
                toolstripPeerModelManager.setSyncEnabled(true);
                toolstripPeerModelManager.addEventListener("rootSet", this._handleToolstripRootSet, this);
            }

            if (this._uiContainer) {
                this._buildContainerModel(this._uiContainer);
                this._listenForContainerChanges(this._uiContainer);
            }

            this._unloadListener = on(window, "beforeunload", function () {
                that.destroy();
            });
        },

        destroy: function () {
            if (this._isDestroying) {
                return;
            }
            this._isDestroying = true;
            MessageService.publish(getMessageChannel(this.id), {type: "terminated"});
            PeerModelManager.cleanup(this.channel);
            if (this._uiContainer && this._builtContainer) {
                this._uiContainer.shutdown();
                MessageService.stop();
            }
            this._isDestroying = false;
        },

        detachFromContainer: function() {
            if (this._clientRoot) {
                this._clientRoot.getChildren().forEach(function (child) {
                    var type = child.getType();
                    if (type === "panel" || type === "documentType" || type === "document") {
                        PeerNodeStatefulBridge.unlink(child);
                        child.destroy();
                    }
                });
            }
            if (this._unloadListener) {
                this._unloadListener.remove();
                delete this._unloadListener;
            }
            PeerModelManager.cleanup(this.clientChannel);
        },

        _handleServerRootSet: function (event) {
            this._serverRoot = event.target;
            if (this._serverRoot.hasProperty("reportErrors") && this._serverRoot.getProperty("reportErrors")) {
                // Add global error listener that publishes message back to server
                var that = this;
                window.addEventListener("error", function(event) {
                    MessageService.publish(getMessageChannel(that.id), {
                        type: "error",
                        message: event.message,
                        stack: event.error.stack
                    });
                })
            }
            this._serverRoot.addEventListener("peerEvent", this._getSafeHandler(this._handleServerPeerEvent), this);
            this._serverRoot.addEventListener("propertySet", this._getSafeHandler(this._handleServerPropertySet), this);
            this._serverRoot.addEventListener("childAdded", this._getSafeHandler(this._handleServerChildAdd), this);
            this._serverRoot.addEventListener("childRemoved", this._getSafeHandler(this._handleServerChildRemove), this);
            this._buildContainerWhenReady();
        },

        _handleToolstripRootSet: function (event) {
            var that = this;
            event.target.addEventListener("childAdded", function (event) {
                var child = event.data.child;
                if (child.getType() === TypeConstants.ORPHAN_ROOT) {
                    child.addEventListener("childAdded", function (event) {
                        // If global tab group register
                        var tabGroup = event.data.child;
                        if (tabGroup.getType() === TypeConstants.TAB_GROUP && !tabGroup.getProperty("contextual")) {
                            var tag = tabGroup.getProperty("tag");
                            if (that._uiContainer) {
                                that._uiContainer.addTabGroup(tag, false);
                            } else {
                                if (!that._pendingGlobalTabGroupTags) {
                                    that._pendingGlobalTabGroupTags = [];
                                }
                                that._pendingGlobalTabGroupTags.push(tag);
                            }
                        }
                    });

                    that._isToolstripRootReady = true;
                    that._buildContainerWhenReady();
                } else if (child.getType() === TypeConstants.STATUS_BAR_ROOT) {
                    // Save the status bar root so that the status bar can be retrieved when
                    // the UIContainer is built.
                    that._statusBarRoot = child;
                    child.addEventListener("childAdded", function (event) {
                        if (that._uiContainer) {
                            var statusBar = event.data.child;
                            that._uiContainer.addStatusBar(statusBar.getProperty("tag"));
                        }
                    });
                }
            });
        },

        _buildContainerWhenReady: function () {
            if (!this._uiContainer && this._serverRoot && (this._isToolstripRootReady || !this.toolstripChannel)) {
                if (this._serverRoot.hasProperty("waitForDebugger") && this._serverRoot.getProperty("waitForDebugger")) {
                    this._buildContainerAfterDebuggerStarted();
                } else {
                    this._buildContainerAfterDelay();
                }
            }
        },

        _buildContainerAfterDebuggerStarted: function () {
            var that = this;
            var poller = setInterval(function () {
                var time = new Date();
                debugger;  // **** Set desired breakpoints and continue ****
                if (new Date() - time > 200) {
                    clearInterval(poller);
                    if (!that._uiContainer) {
                        that._buildContainer();
                    }
                }
            }, 250);
        },

        _buildContainerAfterDelay: function () {
            // If the UIContainer is constructed immediately, its toolstrip construction fails because the UIBuidler
            // is not yet aware of the toolstrip root.  This is probably due to the order of listener execution.
            var that = this;
            setTimeout(function () {
                if (!that._uiContainer) {
                    that._buildContainer();
                }
            }, 0);
        },

        _buildContainer: function () {
            // Construct the UIContainer.
            var containerProperties = {
                appId: this.id,
                uiBuilder: this._uiBuilder,
                tabGroupTags: this._pendingGlobalTabGroupTags,
                reserveDocumentSpace: true, // Different default for AppContainer
                enableCenterCollapse: true  // Should be default but isn't currently (see comment in UIContainer)
            };
            delete this._pendingGlobalTabGroupTags;
            
            var translators = this._createContainerPropertyTranslators();
            var peerProperties = this._serverRoot.getProperties();
            Object.keys(peerProperties).forEach(function (propertyName) {
                peerProperties[propertyName] = PeerNodeStatefulBridge.convertToObjectIfJson(peerProperties[propertyName]);
                var translator = translators[propertyName];
                if (translator) {
                    peerProperties[propertyName] = translator.fromPeer(peerProperties[propertyName]);
                }
            });
            lang.mixin(containerProperties, peerProperties);

            this._uiContainer = new UIContainer("uiroot", containerProperties);
            this._builtContainer = true;

            // If the peer model defines a status bar, add it to the container
            if (this._statusBarRoot) {
                var statusBars = this._statusBarRoot.getChildren();
                if (statusBars.length > 0) {
                    this._uiContainer.addStatusBar(statusBars[0].getProperty("tag"))
                }
            }

            PeerNodeStatefulBridge.link(this._serverRoot, this._uiContainer, translators);
            this._setupContainerCloseApproval(this._serverRoot.getProperty("hasCloseApprover"));

            this._deferredEvents.forEach(function (handler) {
                handler();
            });
        },

        _getSafeHandler: function (handler) {
            return lang.partial(this._handleContainerEvent, handler);
        },

        _handleContainerEvent: function(handler, event) {
            if (this._uiContainer) {
                lang.hitch(this, handler) (event);
            } else {
                this._deferredEvents.push(lang.hitch(this, handler, event));
            }
        },

        _handleServerPeerEvent: function (event) {
            if (event.data.type === "start") {
                this._uiContainer.startup();
            } else if (event.data.type === "closeReply") {
                if (this._pendingCloseQuery && this._pendingCloseQuery.id === event.data.id) {
                    if (event.data.approve) {
                        this._pendingCloseQuery.resolve(true);
                    } else {
                        this._pendingCloseQuery.reject();
                    }
                    delete this._pendingCloseQuery;
                }
            }
        },

        _handleServerPropertySet: function (event) {
            if (event.data.key === "hasCloseApprover") {
                this._setupContainerCloseApproval(event.data.newValue);
            }
        },

        _setupContainerCloseApproval: function (hasCloseApprover) {
            var that = this;
            if (hasCloseApprover) {
                this._containerCloseApprover = function () {
                    that._pendingCloseQuery = new Deferred();
                    that._pendingCloseQuery.id = String(Date.now());
                    var data = {id : that._pendingCloseQuery.id};
                    that._serverRoot.dispatchPeerEvent("closeQuery", null, data);
                    return that._pendingCloseQuery.promise;
                };
                this._uiContainer.addCloseApprover(this._containerCloseApprover);
            } else if (this._containerCloseApprover) {
                this._uiContainer.removeCloseAppover(this._containerCloseApprover);
                delete this._containerCloseApprover;
            }

        },

        _handleServerChildAdd: function (event) {
            var properties, widget, typeProperties;
            var child = event.data.child;
            var that = this;

            if (!child) {
                return;
            }
            switch (child.getType()) {
                case "panel":
                    properties = PeerNodeStatefulBridge.createChildPropertiesFromPeerNode(child);
                    if (properties.factoryPath) {
                        require([properties.factoryPath], function(Factory) {
                            var factory = new Factory();
                            CssLoader.loadFiles(factory.getCssFiles());
                            widget = factory.createWidget(that._uiContainer, PeerNodeStatefulBridge.convertToObjectIfJson(properties.content), properties);
                            that._uiContainer.addPanel(widget, properties, factory, properties.content);
                        });
                    } else {
                        widget = new ContentPane({content: properties.content});
                        this._uiContainer.addPanel(widget, properties);
                    }
                    break;

                case "documentType":
                    properties = PeerNodeStatefulBridge.createDocumentTypePropertiesFromPeerNode(child,
                                 this._createDocumentTypePropertyTranslators());
                    if (!properties.tabGroupTags) {
                        properties.tabGroupTags = [];
                    }
                    this._uiContainer.registerDocumentType(properties.typeId, properties);
                    break;

                case "document":
                    properties = PeerNodeStatefulBridge.createChildPropertiesFromPeerNode(child);
                    typeProperties = this._uiContainer.getDocumentTypeProperties(properties.documentType);
                    if (typeProperties.documentFactory || typeProperties.factoryPath) {
                        if (typeProperties.documentFactory) {
                            this._createAndAddDocument(properties, typeProperties.documentFactory);
                        } else {
                            require([typeProperties.factoryPath], function(Factory) {
                                typeProperties.documentFactory = new Factory();
                                that._createAndAddDocument(properties, typeProperties.documentFactory);
                            });
                        }
                    } else {
                        widget = new ContentPane({content: properties.content});
                        this._uiContainer.addDocument(widget, properties);
                    }
                    this._setupDocumentCloseApproval(child, properties);
                    properties.watch("hasCloseApprover", lang.hitch(this, "setupDocumentCloseApproval", child, properties));
            }
        },

        _createAndAddDocument: function (properties, factory) {
            CssLoader.loadFiles(factory.getCssFiles());
            var widget = factory.createWidget(this._uiContainer, PeerNodeStatefulBridge.convertToObjectIfJson(properties.content), properties);
            this._uiContainer.addDocument(widget, properties);
        },

        _handleServerChildRemove: function (event) {
            var child = event.data.child;
            var that = this;

            if (!child) {
                return;
            }
            switch (child.getType()) {
                case "panel":
                    var widget = this._uiContainer.removePanel(child.getProperty("panelId"));
                    if (widget) {
                        widget.destroyRecursive();
                    }
                    break;

                case "documentType":
                    this._uiContainer.unregisterDocumentType(child.getProperty("typeId"));
                    break;

                case "document":
                    var idOrTitle = child.getProperty("documentId") || child.getProperty("title");
                    var properties = this._uiContainer.getChildProperties(idOrTitle, child.getProperty("documentType"));
                    if (properties) {
                        properties.set("isOpen", false);
                    }
            }
            PeerNodeStatefulBridge.unlink(child);
        },

        _setupDocumentCloseApproval: function (peerNode, properties) {
            if (properties.hasCloseApprover !== undefined) {
                if (properties.hasCloseApprover) {
                    properties.canClose = function () {
                        properties._pendingCloseQuery = new Deferred();
                        properties._pendingCloseQuery.id = String(Date.now());
                        var data = {id : properties._pendingCloseQuery.id};
                        peerNode.dispatchPeerEvent("closeQuery", null, data);
                        return properties._pendingCloseQuery.promise;
                    };
                    peerNode.addEventListener("peerEvent", lang.hitch(this, "_handleDocumentPeerEvent", properties));

                } else if (properties.canClose) {
                    delete properties.canClose;
                }
            }
        },

        _handleDocumentPeerEvent: function (properties, event) {
            if (event.data.type === "closeReply") {
                if (properties._pendingCloseQuery && properties._pendingCloseQuery.id === event.data.id) {
                    if (event.data.approve) {
                        properties._pendingCloseQuery.resolve(true);
                    } else {
                        properties._pendingCloseQuery.reject();
                    }
                    delete properties._pendingCloseQuery;
                }
            }
        },

        _buildContainerModel: function (uiContainer) {
            this._clientRoot = PeerNodeStatefulBridge.createPeerNodeFromUIContainer(this._clientModelManager, uiContainer,
                                                                                    this._createContainerPropertyTranslators());
            var that = this;
            uiContainer.getPanels().forEach(function (panelInfo) {
                PeerNodeStatefulBridge.createPeerNodeFromChildProperties(that._clientRoot, "panel", panelInfo.properties);
            });
            uiContainer.getDocuments().forEach(function (documentInfo) {
                PeerNodeStatefulBridge.createPeerNodeFromChildProperties(that._clientRoot, "document", documentInfo.properties);
            });
            uiContainer.getDocumentTypes().forEach(function (typeInfo) {
                PeerNodeStatefulBridge.createPeerNodeFromDocumentTypeProperties (that._clientRoot, typeInfo.type, typeInfo.properties,
                                                                                 that._createDocumentTypePropertyTranslators());
            });
        },

        _listenForContainerChanges: function (uiContainer) {
            var that = this;
            uiContainer.on(UIContainer.CHILD_ADDED, function (eventData) {
                switch (eventData.childType) {
                    case "panel":
                    case "document":
                        if (!eventData.properties.isDocking) {
                            PeerNodeStatefulBridge.createPeerNodeFromChildProperties(that._clientRoot, eventData.childType, eventData.properties);
                        }
                        break;
                    case "documentType":
                        PeerNodeStatefulBridge.createPeerNodeFromDocumentTypeProperties (that._clientRoot, eventData.typeId, eventData.properties,
                                                                                         that._createDocumentTypePropertyTranslators());
                }
            });
            uiContainer.on(UIContainer.CHILD_REMOVED, function (eventData) {
                var predicate, child;
                switch (eventData.childType) {
                    case "panel":
                        predicate = function (child) {
                            return child.getProperty("panelId") === eventData.properties.panelId;
                        };
                        break;
                    case "document":
                        if (!eventData.properties.isUndocking) {
                            predicate = function (child) {
                                return child.getProperty("documentId") === eventData.properties.documentId ||
                                    child.getProperty("title") === eventData.properties.title;
                            };
                        }
                        break;
                    case "documentType":
                        predicate = function (child) {
                            return child.getProperty("typeId") === eventData.properties.typeId;
                        };
                }
                if (predicate) {
                    child = that._findClientModelChild(eventData.childType, predicate);
                    if (child) {
                        child.destroy();
                    }
                }
            });
        },

        _findClientModelChild: function (childType, predicate) {
            var children = this._clientRoot.getChildren();
            var i, child;
            for (i = 0; i < children.length; i++) {
                child = children[i];
                if (child.getType() === childType && predicate(child)) {
                    return child;
                }
            }
            return null;
        },

        _createContainerPropertyTranslators: function () {
            var that = this;
            var translators = {
                tabGroupTags: PropertyTranslator.ForceArray,
                activeContexts: PropertyTranslator.ForceArray,
                borderOptions: new PropertyTranslator({
                    fromPeer: function(options) {
                        if (options && !Array.isArray(options)) {
                            options = [options];
                        }
                        var translation = {};
                        for (var i = 0; i < options.length; i++) {
                            var regionOptions = JSON.parse(options[i]);
                            translation[regionOptions.region] = regionOptions;
                        }
                        return translation;
                    }
                }),
                contexts: new PropertyTranslator({
                    fromPeer: function(contexts) {
                        if (contexts && !Array.isArray(contexts)) {
                            contexts = [contexts];
                        }
                        for (var i = 0; i < contexts.length; i++) {
                            var context = JSON.parse(contexts[i]);
                            if (context.tabGroupTags && !Array.isArray(context.tabGroupTags)) {
                                context.tabGroupTags = [context.tabGroupTags];
                            }
                            if (context.statusComponentTags && !Array.isArray(context.statusComponentTags)) {
                                context.statusComponentTags = [context.statusComponentTags];
                            }
                            if (context.panelIds && !Array.isArray(context.panelIds)) {
                                context.panelIds = [context.panelIds];
                            }
                            contexts[i] = context;
                        }
                        return contexts;
                    }
                }),

                selectedChild: new PropertyTranslator({
                    toPeer: function (child) {
                        if (!child) {
                            return {tag: "", title: "", documentGroupTag: ""};
                        }
                        var properties = that._uiContainer.getChildProperties(child);
                        return {
                            tag: properties.panelId || properties.documentId,
                            title: properties.title,
                            documentGroupTag: properties.documentType
                        };
                    },
                    fromPeer: function (childInfo) {
                        return that._uiContainer.getChildWidget(childInfo.tag || childInfo.title, childInfo.documentGroupTag);
                    }
                }),

                lastSelectedDocument: new PropertyTranslator({
                    toPeer: function (document) {
                        if (!document) {
                            return {tag: "", title: "", documentGroupTag: ""};
                        }
                        var properties = that._uiContainer.getChildProperties(document);
                        return {
                            tag: properties.documentId,
                            title: properties.title,
                            documentGroupTag: properties.documentType
                        };
                    }
                }),

                selectedToolstripTab: new PropertyTranslator ({
                    toPeer: function (tab) {
                        // Omits extraneous "id" property
                        return !tab ? {tag: "", title: ""} : ((tab.title && tab.tag) ? {tag: tab.tag, title: tab.title} : null);
                    }
                }),

                documentLayout: new PropertyTranslator({
                    fromPeer: function (documentLayout) {
                        var i;
                        var that = this;
                        // On the MATLAB side jsonencode converts arrays containing a single object to just the object.
                        // The following code inserts such objects back into arrays.
                        if (documentLayout.columnWeights && !Array.isArray(documentLayout.columnWeights)) {
                            delete documentLayout.columnWeights;  // Single column defaults to weight = 1
                        }
                        if (documentLayout.rowWeights && !Array.isArray(documentLayout.rowWeights)) {
                            delete documentLayout.rowWeights;  // Single row defaults to weight = 1
                        }
                        if (documentLayout.tileCoverage) {
                            var dimensions = documentLayout.gridDimensions || documentLayout.gridSize;
                            if (dimensions.w === 1 && dimensions.h === 1) {
                                documentLayout.tileCoverage = [[]];
                            } else if (dimensions.h === 1) {
                                documentLayout.tileCoverage = [documentLayout.tileCoverage];
                            } else if (dimensions.w === 1) {
                                for (i = 0; i < documentLayout.tileCoverage.length; i++) {
                                    if (!Array.isArray(documentLayout.tileCoverage[i])) {
                                        documentLayout.tileCoverage[i] = [documentLayout.tileCoverage[i]];
                                    }
                                }
                            }
                        }
                        if (documentLayout.tileOccupancy) {
                            if (!Array.isArray(documentLayout.tileOccupancy)) {
                                documentLayout.tileOccupancy = [documentLayout.tileOccupancy];
                            }
                            for (i = 0; i < documentLayout.tileOccupancy.length; i++) {
                                if (!Array.isArray(documentLayout.tileOccupancy[i].children)) {
                                    documentLayout.tileOccupancy[i].children = [documentLayout.tileOccupancy[i].children];
                                }
                            }
                        }
                        if (documentLayout.nestings) {
                            if (!Array.isArray(documentLayout.nestings)) {
                                documentLayout.nestings = [documentLayout.nestings];
                            }
                            documentLayout.nestings.forEach(function (nesting) {
                                that.fromPeer(nesting);
                            });
                        }
                        return documentLayout;
                    }
                }),

                panelLayout: new PropertyTranslator({
                    fromPeer: function (panelLayout) {
                        ["left", "right", "bottom"].forEach(function (region) {
                            var regionInfo = panelLayout[region];
                            if (regionInfo) {
                                if (regionInfo.children && !Array.isArray(regionInfo.children)) {
                                    regionInfo.children = [regionInfo.children];
                                }
                            }
                        });
                        return panelLayout;
                    }
                }),

                layout: new PropertyTranslator({
                    fromPeer: function (layout) {
                        if (layout.documentLayout) {
                            layout.documentLayout = translators.documentLayout.fromPeer(layout.documentLayout);
                        }
                        if (layout.panelLayout) {
                            layout.panelLayout = translators.panelLayout.fromPeer(layout.panelLayout);
                        }
                        return layout;
                    }
                }),

                defaultLayout: new PropertyTranslator({
                    fromPeer: function (layout) {
                        return translators.layout.fromPeer(layout);
                    }
                })
            };
            return translators;
        },

        _createDocumentTypePropertyTranslators: function () {
            var that = this;
            var translators = {
                lastSelected: new PropertyTranslator({
                    toPeer: function (child) {
                        if (!child) {
                            return {tag: "", title: ""};
                        }
                        var properties = that._uiContainer.getChildProperties(child);
                        return {
                            tag: properties.panelId || properties.documentId,
                            title: properties.title
                        };
                    }
                })
            };
            return translators;
        }
    });

    var getMessageChannel = function (containerOrId) {
        var id = typeof(containerOrId) === "string" ? containerOrId : containerOrId.appId;
        return "/" + id + "/uicontainer";
    };

    UIContainerPeer.enableAttach = function (uiContainer) {
        // Subscribe to message service to allow server-side creation of wrapping AppContainer
        if (!MessageService.isRunning()) {
            MessageService.start();
        }
        messageChannel = getMessageChannel(uiContainer);
        MessageService.subscribe(messageChannel, function(message) {
            switch(message.data.type) {
                case "attach":
                    if (!uiContainer._peer) {
                        uiContainer._peer = new UIContainerPeer({
                            serverChannel: message.data.serverChannel,
                            clientChannel: message.data.clientChannel,
                            uiContainer: uiContainer});
                    }
                    break;
                case "detach":
                    if (uiContainer._peer) {
                        uiContainer._peer.detachFromContainer();
                        uiContainer._peer = null;
                    }
                    break;
                case "openChild":
                    ChildWindowManager.open({page: message.data.page}).then(
                        function() {},  // No success response
                        function() {}   // No error response (prevents console message)
                    );
            }
        });
        MessageService.publish(messageChannel, {type: "started"});
    };

    exports.UIContainerPeer = UIContainerPeer;
    exports.enableAttach  =  UIContainerPeer.enableAttach;

    return UIContainerPeer;
});